// Lazily loads the Google Maps JavaScript API only when a region map opens, so
// the U.S. and state SVG views never count as billable map loads.
const CALLBACK_NAME = '__gravvisoftGoogleMapsReady';
let loadPromise = null;
const authFailureListeners = new Set();
let authFailed = false;

export function googleMapsApiKey() {
  return String(process.env.REACT_APP_GOOGLE_MAPS_KEY || '').trim();
}

export function loadGoogleMaps(apiKey = googleMapsApiKey()) {
  if (typeof window !== 'undefined' && window.google?.maps?.Map) {
    return Promise.resolve(window.google.maps);
  }
  if (!apiKey) return Promise.reject(new Error('Google Maps key is not configured.'));
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    window[CALLBACK_NAME] = () => {
      delete window[CALLBACK_NAME];
      window.google.maps.importLibrary('maps')
        .then(() => resolve(window.google.maps))
        .catch(reject);
    };
    // Google invokes this when the key or referrer is rejected, usually after load.
    window.gm_authFailure = () => {
      authFailed = true;
      const error = new Error('Google Maps rejected the configured key for this site.');
      reject(error);
      authFailureListeners.forEach((listener) => listener(error));
    };
    const script = document.createElement('script');
    const params = new URLSearchParams({ key: apiKey, v: 'weekly', loading: 'async', callback: CALLBACK_NAME });
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.onerror = () => {
      loadPromise = null;
      script.remove();
      reject(new Error('Could not load Google Maps.'));
    };
    document.head.appendChild(script);
  });
  return loadPromise;
}

export function onGoogleMapsAuthFailure(listener) {
  if (authFailed) {
    listener(new Error('Google Maps rejected the configured key for this site.'));
    return () => {};
  }
  authFailureListeners.add(listener);
  return () => authFailureListeners.delete(listener);
}
